import { useLoaderData } from '@remix-run/react';
import {
    pxToRem,
    Grid,
    Paragraph,
    Accordion,
    useStyles,
    Subheader,
    useAppDisplayModeContext,
} from '@archipro-design/aria';
import { AngleUp, AngleDown } from '@archipro-design/icons';

import * as S from './CustomTabs.style';
import type { InfoPageLoaderData } from '@modules/professional/api/info-page-loader.server';
import { useWebsiteTrackingProxy } from '~/modules/tracking/hook/useWebsiteTrackingProxy';

export const CustomTabs: React.FC = () => {
    const { libraryTabs, professional } = useLoaderData<InfoPageLoaderData>();
    const { mobile } = useAppDisplayModeContext();
    const { css } = useStyles();
    const clickProxy = useWebsiteTrackingProxy({
        professionalID: professional.id,
        professionalTitle: professional.title,
        itemType: 'Professional',
    });

    if (!libraryTabs?.length) return null;

    const tabs = libraryTabs.map((tab) => ({
        key: tab.ID,
        title: tab.Title,
        content: (
            <Paragraph
                className={css(S.AccordionContent)}
                dangerouslySetInnerHTML={{ __html: tab.Content ?? '' }}
                onClick={clickProxy}
                onContextMenu={clickProxy}
                style={{
                    wordBreak: 'break-word',
                }}
            />
        ),
    }));

    return (
        <Accordion
            panels={tabs}
            className={css(S.AccordionWrap)}
            renderPanelTitle={(Component, itemProps) => (
                <Component
                    {...itemProps}
                    className={css(S.AccordionTitle)}
                    key={`${itemProps.index}-accordian`}
                    contentWrapper={
                        <CustomTabHandle
                            isMobile={mobile}
                            key={itemProps.index}
                            active={Boolean(itemProps.active)}
                            content={itemProps.content?.toString() ?? ''}
                        />
                    }
                />
            )}
        />
    );
};

const CustomTabHandle: React.FC<{
    content: string;
    active: boolean;
    isMobile: boolean;
}> = ({ content, active, isMobile }) => {
    const { css } = useStyles();

    return (
        <Grid
            columns={`1fr ${pxToRem(58)}`}
            className={css(S.AccordionTitleInner)}
        >
            {content && (
                <Subheader
                    variant="04"
                    className={isMobile ? css(S.Subheader) : ''}
                >
                    {content.toString()}
                </Subheader>
            )}
            <div className={css(S.AccordionIconWrap)}>
                {active ? (
                    <AngleUp
                        variables={{
                            '1xSize': pxToRem(isMobile ? 24 : 20),
                        }}
                    />
                ) : (
                    <AngleDown
                        variables={{
                            '1xSize': pxToRem(isMobile ? 24 : 20),
                        }}
                    />
                )}
            </div>
        </Grid>
    );
};
